import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search, FileText, ChevronDown, ChevronRight, Trash2, ExternalLink } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Proposal,
  deleteProposal,
  getProposal,
  getProposalEvents,
  isProposalExpired as isProposalExpiredByValidUntil,
  searchProposals,
} from '../lib/proposals';
import { useProposals } from '../hooks/useProposals';
import { useBusiness } from '../contexts/BusinessContext';
import EmptyState from '../components/EmptyState';
import CreateProposalModal from '../components/CreateProposalModal';
import ProposalDetailsModal from '../components/ProposalDetailsModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import PermissionGuard from '../components/PermissionGuard';
import { ProposalSkeletonList } from '../components/ProposalSkeleton';

const statusOptions = [
  { label: 'All Proposals', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'Sent', value: 'sent' },
  { label: 'Viewed', value: 'viewed' },
  { label: 'Approved', value: 'approved' },
  { label: 'Declined', value: 'declined' }
];

interface ProposalsProps {
  readOnly?: boolean;
}

function Proposals({ readOnly = false }: ProposalsProps) {
  const { selectedBusiness } = useBusiness();
  const location = useLocation();
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);
  const [dateFilter, setDateFilter] = useState<'all-time' | 'this-month' | 'last-month' | 'this-year'>('all-time');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);
  const processedProposalIdRef = useRef<string | null>(null);
  const { proposals, loading, loadingMore, error, hasMore, totalCount, refetch, loadMore } = useProposals(selectedBusiness?.id);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [proposalToDelete, setProposalToDelete] = useState<Proposal | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [proposalEvents, setProposalEvents] = useState<Record<string, any[]>>({});
  const [searchResults, setSearchResults] = useState<Proposal[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const navigate = useNavigate();

  // Helper function to check if proposal is expired
  const isProposalExpired = (proposal: Proposal) =>
    isProposalExpiredByValidUntil(proposal.valid_until);

  // List page only keeps ~30 rows in memory — search older proposals on the server.
  useEffect(() => {
    const q = searchQuery.trim();
    if (!selectedBusiness?.id || !q) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }

    let cancelled = false;
    const handle = window.setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await searchProposals(selectedBusiness.id, q);
        if (!cancelled) setSearchResults(results);
      } catch (err) {
        console.error('Proposal search failed:', err);
        if (!cancelled) setSearchResults([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [searchQuery, selectedBusiness?.id]);

  // Calculate the total for a single offer (all services including optional)
  const calcOfferTotal = (offer: any): number => {
    const services = offer.services || [];
    const products = (offer as any).products || [];

    const serviceSubtotal = services.reduce((sum: number, s: any) => sum + (s.price || 0), 0);
    const serviceDiscount = services.reduce((sum: number, s: any) => sum + (s.discount_amount || 0), 0);
    const productSubtotal = products.reduce((sum: number, p: any) => sum + ((p.price || 0) * (p.quantity || 1)), 0);
    const productDiscount = products.reduce((sum: number, p: any) => sum + (p.discount_amount || 0), 0);

    const discountedSubtotal = (serviceSubtotal + productSubtotal) - (serviceDiscount + productDiscount);
    const taxAmount = services.reduce((sum: number, s: any) => {
      const price = (s.price || 0) - (s.discount_amount || 0);
      return sum + (s.tax_exempt ? 0 : price * ((s.tax_rate || 0) / 100));
    }, 0) + products.reduce((sum: number, p: any) => {
      const price = ((p.price || 0) * (p.quantity || 1)) - (p.discount_amount || 0);
      return sum + (p.tax_exempt ? 0 : price * ((p.tax_rate || 0) / 100));
    }, 0);

    return Math.round((discountedSubtotal + taxAmount) * 100) / 100;
  };

  // Calculate total based on selected services only (for approved offer)
  const calcSelectedTotal = (offer: any): number => {
    const services = (offer.services || []).filter((s: any) => {
      const selectable = s.isSelectable || s.is_selectable;
      return !selectable || (selectable && s.is_selected);
    });
    const products = ((offer as any).products || []).filter((p: any) => {
      const selectable = p.isSelectable || p.is_selectable;
      return !selectable || (selectable && p.is_selected);
    });

    const serviceSubtotal = services.reduce((sum: number, s: any) => sum + (s.price || 0), 0);
    const serviceDiscount = services.reduce((sum: number, s: any) => sum + (s.discount_amount || 0), 0);
    const productSubtotal = products.reduce((sum: number, p: any) => sum + ((p.price || 0) * (p.quantity || 1)), 0);
    const productDiscount = products.reduce((sum: number, p: any) => sum + (p.discount_amount || 0), 0);

    const discountedSubtotal = (serviceSubtotal + productSubtotal) - (serviceDiscount + productDiscount);
    const taxAmount = services.reduce((sum: number, s: any) => {
      const price = (s.price || 0) - (s.discount_amount || 0);
      return sum + (s.tax_exempt ? 0 : price * ((s.tax_rate || 0) / 100));
    }, 0) + products.reduce((sum: number, p: any) => {
      const price = ((p.price || 0) * (p.quantity || 1)) - (p.discount_amount || 0);
      return sum + (p.tax_exempt ? 0 : price * ((p.tax_rate || 0) / 100));
    }, 0);

    return Math.round((discountedSubtotal + taxAmount) * 100) / 100;
  };

  const getDisplayTotal = (proposal: Proposal): number => {
    if (proposal.status === 'approved' || proposal.status === 'booked') {
      const approvedOffer = proposal.offers?.find(o => o.status === 'approved');
      if (approvedOffer) return calcSelectedTotal(approvedOffer);
      return proposal.total_amount;
    }
    // For non-approved proposals, show max potential of any single offer
    if (proposal.offers && proposal.offers.length > 0) {
      return Math.max(...proposal.offers.map(o => calcOfferTotal(o)));
    }
    return proposal.total_amount;
  };

  const isMaxPotential = (proposal: Proposal): boolean => {
    return (proposal.status !== 'approved' && proposal.status !== 'booked') 
      && (proposal.offers?.length ?? 0) > 1;
  };

  // Helper function to get display status
  const getDisplayStatus = (proposal: Proposal) => {
    if (proposal.status === 'approved') return proposal.status;
    if (isProposalExpired(proposal)) return 'expired';
    return proposal.status;
  };

  // Helper function to get status timestamp
  const getStatusTimestamp = (proposal: Proposal, status: string) => {
    const events = proposalEvents[proposal.id] || [];
    
    // For expired status, use the valid_until date
    if (status === 'expired' && proposal.valid_until) {
      return new Date(proposal.valid_until).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    
    // Look for status change events
    const statusEvent = events.find(event => 
      event.event_type === 'status_changed' && 
      event.metadata?.new_status === status
    );
    
    if (statusEvent) {
      return new Date(statusEvent.event_timestamp).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    
    // For created status, use created_at
    if (status === 'draft') {
      return new Date(proposal.created_at).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    
    return null;
  };

  // Check for proposalId in URL parameters on mount and when location/search changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const proposalId = urlParams.get('proposalId');
    
    // Only process if we have a proposalId and haven't already processed this specific one
    if (proposalId && processedProposalIdRef.current !== proposalId) {
      processedProposalIdRef.current = proposalId;
      
      // Clean up URL immediately to prevent re-triggering
      window.history.replaceState({}, document.title, '/proposals');
      
      // Find the proposal in the current list
      const proposal = proposals.find(p => p.id === proposalId);
      if (proposal) {
        setSelectedProposal(proposal);
        setIsDetailsModalOpen(true);
      } else {
        // If proposal not in list, try loading it directly
        const loadProposal = async () => {
          try {
            const proposalData = await getProposal(proposalId);
            if (proposalData) {
              setSelectedProposal(proposalData);
              setIsDetailsModalOpen(true);
              // Refetch proposals to include the new one in the list (but don't wait for it)
              setTimeout(() => refetch().catch(console.error), 100);
            }
          } catch (err) {
            console.error('Error loading proposal from URL:', err);
            processedProposalIdRef.current = null; // Reset on error so user can try again
          }
        };
        loadProposal();
      }
    } else if (!proposalId) {
      // Reset the ref when proposalId is removed from URL
      processedProposalIdRef.current = null;
    }
  }, [location.search]); // Only depend on location.search to avoid infinite loops

  // Load proposal events when proposals change
  useEffect(() => {
    const loadProposalEvents = async () => {
      if (!proposals.length) return;
      
      const eventsMap: Record<string, any[]> = {};
      
      // Load events for all proposals
      await Promise.all(
        proposals.map(async (proposal) => {
          try {
            const events = await getProposalEvents(proposal.id);
            eventsMap[proposal.id] = events;
          } catch (error) {
            console.error(`Error loading events for proposal ${proposal.id}:`, error);
            eventsMap[proposal.id] = [];
          }
        })
      );
      
      setProposalEvents(eventsMap);
    };
    
    loadProposalEvents();
  }, [proposals]);

  // Date range (by proposal created date), matching the Invoices filter options.
  const dateRange = (() => {
    const now = new Date();
    const startOfDay = (y: number, m: number, d: number) => new Date(y, m, d, 0, 0, 0, 0);
    const endOfDay = (y: number, m: number, d: number) => new Date(y, m, d, 23, 59, 59, 999);
    switch (dateFilter) {
      case 'this-month':
        return { start: startOfDay(now.getFullYear(), now.getMonth(), 1), end: endOfDay(now.getFullYear(), now.getMonth(), now.getDate()) };
      case 'last-month': {
        const m = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
        const y = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
        const lastDay = new Date(y, m + 1, 0).getDate();
        return { start: startOfDay(y, m, 1), end: endOfDay(y, m, lastDay) };
      }
      case 'this-year':
        return { start: startOfDay(now.getFullYear(), 0, 1), end: endOfDay(now.getFullYear(), now.getMonth(), now.getDate()) };
      case 'all-time':
      default:
        return { start: null as Date | null, end: null as Date | null };
    }
  })();

  const proposalsForFilter = searchQuery.trim() ? (searchResults ?? []) : proposals;

  const filteredProposals = proposalsForFilter.filter((proposal) => {
    if (selectedStatus.value !== 'all' && proposal.status !== selectedStatus.value) {
      return false;
    }

    if (dateRange.start && dateRange.end) {
      const created = new Date(proposal.created_at);
      if (Number.isNaN(created.getTime()) || created < dateRange.start || created > dateRange.end) {
        return false;
      }
    }

    return true;
  });

  // Handle navigation from customer details modal with proposal ID or full proposal object
  useEffect(() => {
    const handleProposalNavigation = async () => {
      const state = location.state as { selectedProposalId?: string; selectedProposal?: Proposal } | null;
      
      // If full proposal object is passed, use it directly
      if (state?.selectedProposal) {
        setSelectedProposal(state.selectedProposal);
        setIsDetailsModalOpen(true);
        // Clear the navigation state to prevent reopening on refresh
        window.history.replaceState({}, document.title);
        return;
      }
      
      // Otherwise, fetch by ID if provided
      if (state?.selectedProposalId && selectedBusiness?.id) {
        try {
          // Fetch the specific proposal details
          const proposal = await getProposal(state.selectedProposalId);
          setSelectedProposal(proposal);
          setIsDetailsModalOpen(true);
          
          // Clear the navigation state to prevent reopening on refresh
          window.history.replaceState({}, document.title);
        } catch (err) {
          console.error('Error fetching proposal from navigation:', err);
        }
      }
    };

    handleProposalNavigation();
  }, [location.state, selectedBusiness?.id]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-700';
      case 'sent':
        return 'bg-blue-100 text-blue-700';
      case 'viewed':
        return 'bg-purple-100 text-purple-700';
      case 'approved':
        return 'bg-green-100 text-green-700';
      case 'declined':
      case 'rejected':
        return 'bg-red-100 text-red-700';
      case 'expired':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };


  const fmtMoney = (n: number) =>
    '$' + (Math.round((n || 0) * 100) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return (
    <div 
      className="flex flex-col h-full overflow-y-auto bg-gray-50 min-h-screen" 
      data-proposals-page
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        margin: 0,
        padding: 0
      }}>

      {/* Filters and Search Section - iOS Style */}
      <div className="bg-white border-b border-gray-200/60 px-4 md:px-6 py-3 sticky top-0 z-30" style={{
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)'
      }}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-2.5">
          {/* Search Bar */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search customer, proposal, email"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-[15px] bg-gray-100 border-0 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all duration-150"
              style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
            />
          </div>

          {/* Date Filter */}
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value as typeof dateFilter)}
            className="appearance-none bg-gray-100 border-0 rounded-[10px] px-3.5 py-2.5 text-[15px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-150"
            style={{ minHeight: '36px', letterSpacing: '-0.01em' }}
          >
            <option value="all-time">All Time</option>
            <option value="this-month">This Month</option>
            <option value="last-month">Last Month</option>
            <option value="this-year">This Year</option>
          </select>

          {/* Status Filter */}
          <select
            value={selectedStatus.value}
            onChange={(e) => setSelectedStatus(statusOptions.find(o => o.value === e.target.value) || statusOptions[0])}
            className="appearance-none bg-gray-100 border-0 rounded-[10px] px-3.5 py-2.5 text-[15px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-150"
            style={{ minHeight: '36px', letterSpacing: '-0.01em' }}
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>

          {/* New Proposal Button */}
          {!readOnly && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="flex-shrink-0 inline-flex items-center justify-center px-4 py-2.5 text-[15px] font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-[10px] transition-all duration-150 touch-manipulation shadow-sm"
              style={{ minHeight: '36px', WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              New
            </button>
          )}
        </div>
      </div>

      {/* Main Content Container */}
      <div className="flex-1 px-4 pb-24">
        {loading || (searchQuery.trim() && searchLoading && searchResults === null) ? (
          <div className="pt-2">
            <ProposalSkeletonList count={10} isMobile={false} />
          </div>
        ) : filteredProposals.length === 0 ? (
          <div className="flex items-center justify-center h-64 px-4">
            <EmptyState
              icon={<FileText className="w-8 h-8 text-gray-400" />}
              title={searchQuery ? 'No matching proposals' : 'No proposals yet'}
              description={
                searchQuery
                  ? 'Try adjusting your search terms'
                  : 'Start creating proposals to send to your customers.'
              }
              actionLabel={searchQuery ? 'Clear search' : 'Create Proposal'}
              onAction={() => (searchQuery ? setSearchQuery('') : setIsCreateModalOpen(true))}
            />
          </div>
        ) : (
          <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden divide-y divide-gray-100 mt-2">
            {filteredProposals.map((proposal) => {
              const displayStatus = getDisplayStatus(proposal);
              const expanded = expandedId === proposal.id;
              const isExpired = displayStatus === 'expired';
              const customerName = `${proposal.customer?.first_name || ''} ${proposal.customer?.last_name || ''}`.trim() || 'Unknown';
              const validUntil = proposal.valid_until
                ? new Date(proposal.valid_until).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : null;
              const ts = getStatusTimestamp(proposal, displayStatus);
              const statusLabel = displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1);
              return (
                <div key={proposal.id} className={isExpired ? 'opacity-70' : ''}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setExpandedId(expanded ? null : proposal.id)}
                    onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setExpandedId(expanded ? null : proposal.id); } }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    {expanded ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="text-[15px] font-semibold text-gray-900 truncate">{customerName}</div>
                      <div className="text-[13px] text-gray-700 truncate mt-0.5">{proposal.title}</div>
                      <div className="mt-0.5">
                        {validUntil ? (
                          <span className={`text-[12px] ${isExpired ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
                            {isExpired ? 'Expired' : 'Valid until'} {validUntil}
                          </span>
                        ) : ts ? (
                          <span className="text-[12px] text-gray-500">{ts}</span>
                        ) : null}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-[15px] font-bold text-gray-900">{fmtMoney(getDisplayTotal(proposal))}</div>
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-medium ${getStatusColor(displayStatus)}`}>{statusLabel}</span>
                    </div>
                    <button
                      type="button"
                      onClick={(ev) => { ev.stopPropagation(); setSelectedProposal(proposal); setIsDetailsModalOpen(true); }}
                      className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 flex-shrink-0 transition-colors"
                      title="Open proposal"
                      aria-label="Open proposal"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>

                  {expanded && (
                    <div className="px-4 pb-3 pl-11 text-[12px]">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                        <KV label="Status" value={statusLabel} />
                        <KV label={isMaxPotential(proposal) ? 'Max potential' : 'Total'} value={fmtMoney(getDisplayTotal(proposal))} valueClass="text-gray-900 font-semibold" />
                        {validUntil && <KV label="Valid until" value={validUntil} valueClass={isExpired ? 'text-red-600 font-semibold' : 'text-gray-900 font-medium'} />}
                        {ts && <KV label={displayStatus === 'expired' ? 'Expired' : 'Updated'} value={ts} />}
                        <KV label="Offers" value={String(proposal.offers?.length ?? 0)} />
                      </div>
                      <div className="pt-2 flex items-center gap-4">
                        <button type="button" onClick={() => { setSelectedProposal(proposal); setIsDetailsModalOpen(true); }} className="text-[13px] font-medium text-blue-600 hover:text-blue-700">View proposal →</button>
                        {!readOnly && (
                          <button type="button" onClick={() => { setProposalToDelete(proposal); setIsDeleteModalOpen(true); }} className="text-[13px] font-medium text-red-600 hover:text-red-700 inline-flex items-center gap-1">
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Load More Button */}
        {!searchQuery.trim() && hasMore && filteredProposals.length > 0 && (
          <div className="mt-4 mb-24 flex justify-center px-4">
            <button
              onClick={loadMore}
              disabled={loadingMore}
              className="inline-flex items-center px-6 py-2.5 text-[15px] font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-[10px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150 touch-manipulation shadow-sm"
              style={{ 
                minHeight: '36px',
                WebkitTapHighlightColor: 'transparent',
                fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                letterSpacing: '-0.01em'
              }}
            >
              {loadingMore ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                  Loading...
                </>
              ) : (
                'Load More'
              )}
            </button>
          </div>
        )}
      </div>

      <CreateProposalModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={(createdProposal) => {
          setIsCreateModalOpen(false);
          refetch();
          
          // Show the details modal for the newly created proposal
          setSelectedProposal(createdProposal);
          setIsDetailsModalOpen(true);
        }}
      />

      <ProposalDetailsModal
        isOpen={isDetailsModalOpen && selectedProposal !== null}
        onClose={() => {
          setIsDetailsModalOpen(false);
          setSelectedProposal(null);
          processedProposalIdRef.current = null; // Reset ref when modal closes
        }}
        proposal={selectedProposal!}
        onUpdate={refetch}
        readOnly={readOnly}
        onDelete={refetch}
      />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setProposalToDelete(null);
        }}
        onConfirm={async () => {
          if (!proposalToDelete) return;
          try {
            setDeleteLoading(true);
            await deleteProposal(proposalToDelete.id);
            await refetch();
            setIsDeleteModalOpen(false);
            setProposalToDelete(null);
          } catch (err) {
            console.error('Error deleting proposal:', err);
            // Error state is now managed by the hook
          } finally {
            setDeleteLoading(false);
          }
        }}
        title="Delete Proposal"
        message={`Are you sure you want to delete this proposal? This action cannot be undone.`}
        loading={deleteLoading}
      />
    </div>
  );
}

function KV({ label, value, valueClass = 'text-gray-900 font-medium' }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="flex items-baseline gap-1.5 min-w-0">
      <span className="text-gray-500 flex-shrink-0">{label}</span>
      <span className={`${valueClass} truncate`}>{value}</span>
    </div>
  );
}

// Wrap with PermissionGuard
function ProposalsWithPermission({ readOnly = false }: ProposalsProps) {
  return (
    <PermissionGuard permission="proposals.pageAccess">
      <Proposals readOnly={readOnly} />
    </PermissionGuard>
  );
}

export default ProposalsWithPermission;